/**
 * Owner-group ("chain") Cost Report Analysis — cost-center centric.
 *
 * For each HCRIS cost center: how many of the chain's facilities declare a
 * related party, and the market distribution (All / RP-only / No-RP) with every
 * facility in the chain plotted — so you see where the chain's facilities fall,
 * the same three-dimension comparison as the facility view. Unifies "declared"
 * and "hidden" related parties into one distributional view.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Box, Typography, CircularProgress, Tooltip, Chip } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import api from '../../api/axios';
import { curveFromQuantiles } from './costReportCurve';

interface Dist { p10:number|null; p25:number|null; p30:number|null; median:number|null; p70:number|null; p75:number|null; p90:number|null; count:number|null; }
interface Marker { ccn:string; name:string; total:number|null; resident_days:number|null; value:number|null; has_rp:boolean; status:'red'|'yellow'|'green'|null; pctile_all:number|null; pctile_rp:number|null; pctile_no_rp:number|null; }
interface CostCenter { key:string; label:string; total_cost:number; num_facilities:number; num_with_rp:number; num_above_p90:number; num_above_p70:number; distribution:{all:Dist|null;rp_only:Dist|null;no_rp:Dist|null}; facility_markers:Marker[]; }
interface Payload { group_id:string; state:string|null; years:number[]; year:number; num_facilities:number; cost_centers:CostCenter[]; }

const STATUS_COLOR:Record<string,string> = { red:'#c62828', yellow:'#ef6c00', green:'#2e7d32' };
const GROUP_COLOR = { all:'#78909c', rp_only:'#8e24aa', no_rp:'#00897b' };
const money = (v:number|null|undefined) => v==null?'—':`$${v.toFixed(2)}`;
const usd = (v:number|null|undefined) => v==null?'—':(Math.abs(v)>=1e6?`$${(v/1e6).toFixed(2)}M`:Math.abs(v)>=1e3?`$${Math.round(v/1e3)}K`:`$${Math.round(v)}`);
const ord = (n:number) => { const s=['th','st','nd','rd'], v=n%100; return n+(s[(v-20)%10]||s[v]||s[0]); };
const pct = (p:number|null|undefined) => p==null?'—':ord(Math.round(p));


/** Market curves (All/RP-only/No-RP) with every chain facility plotted. */
const ChainCurve: React.FC<{ cc:CostCenter; width:number; height:number; visibleKeys?:string[] }> = ({ cc, width:W, height:H, visibleKeys }) => {
  const d = cc.distribution;
  const groups = [['all',d.all],['rp_only',d.rp_only],['no_rp',d.no_rp]] as const;
  const p90s = groups.map(([,g])=>g?.p90??0).filter(x=>x>0);
  if(!p90s.length) return <Typography variant="caption" color="text.disabled">no benchmark</Typography>;
  const domain = Math.max(...p90s)*1.55 || 1;
  const xp=(v:number|null|undefined)=> v==null?null:Math.min(W,Math.max(0,v/domain*W));
  return (
    <svg width={W} height={H+16} style={{display:'block',overflow:'visible'}}>
      <line x1={0} y1={H} x2={W} y2={H} stroke="#e0e0e0" />
      {groups.map(([k,g])=>{ if(visibleKeys && !visibleKeys.includes(k)) return null; const paths=g?curveFromQuantiles(g,domain,W,H):null; if(!paths) return null; const c=GROUP_COLOR[k];
        return <g key={k}>{k==='all'&&<path d={paths.area} fill={c} fillOpacity={0.14}/>}<path d={paths.line} fill="none" stroke={c} strokeWidth={1.75} strokeDasharray={k==='rp_only'?'5 3':k==='no_rp'?'1.5 2.5':undefined}/></g>; })}
      {d.all && ([['p70','#ef6c00',1.25,'3 3'],['p90','#c62828',2.5,'6 3']] as const).map(([key,col,w,dash])=>{ const v=(d.all as any)?.[key]; const x=xp(v); return x==null?null:<g key={key}><line x1={x} y1={H*0.14} x2={x} y2={H} stroke={col} strokeWidth={w} strokeDasharray={dash} opacity={0.9}/>{H>60 && <text x={x} y={H*0.11} textAnchor="middle" fontSize={10} fontWeight={800} fill={col}>{key}</text>}</g>; })}
      {cc.facility_markers.filter(m=>m.value!=null).map((m,i)=>{ const x=xp(m.value); if(x==null)return null; const col=m.status?STATUS_COLOR[m.status]:'#9e9e9e';
        return <Tooltip key={m.ccn+i} arrow title={`${m.ccn} ${m.name}: ${money(m.value)}/day · ${pct(m.pctile_all)} pctile (all)${m.has_rp?' · related party':''}`}>
          {m.has_rp
            ? <polygon points={`${x},${H-16} ${x-4.5},${H-7} ${x+4.5},${H-7}`} fill={col} fillOpacity={0.85}/>
            : <circle cx={x} cy={H-9} r={3.5} fill="none" stroke={col} strokeWidth={1.6}/>}
        </Tooltip>; })}
    </svg>
  );
};

/** Small red/yellow/green count strip. */
const Sev: React.FC<{ r:number; y:number; g:number }> = ({ r, y, g }) => (
  <Box sx={{ display:'flex', gap:0.75, alignItems:'center' }}>
    {[['red',r],['yellow',y],['green',g]].map(([k,n])=> (n as number)>0 ? (
      <Box key={k as string} sx={{ display:'flex', alignItems:'center', gap:0.4 }}>
        <Box sx={{ width:8, height:8, borderRadius:'50%', backgroundColor:STATUS_COLOR[k as string] }} />
        <Typography sx={{ fontSize:'0.7rem', color:'text.secondary' }}>{n as number}</Typography>
      </Box>
    ) : null)}
  </Box>
);

const CostCenterRow: React.FC<{ cc:CostCenter; year:number }> = ({ cc, year }) => {
  const [open,setOpen] = useState(false);
  const [visible,setVisible] = useState<Record<string,boolean>>({ all:true, rp_only:true, no_rp:true });
  const sortedFacs = useMemo(()=> [...cc.facility_markers].filter(m=>m.value!=null).sort((a,b)=>(b.pctile_all??-1)-(a.pctile_all??-1)), [cc]);
  const green = cc.num_facilities - cc.num_above_p70;
  const yellow = cc.num_above_p70 - cc.num_above_p90;
  const pcell = (p:number|null) => <span style={{ color: p!=null && p>=90 ? STATUS_COLOR.red : p!=null && p>=70 ? STATUS_COLOR.yellow : undefined, fontWeight: p!=null && p>=70 ? 700 : 400 }}>{pct(p)}</span>;
  return (
    <Box sx={{ borderBottom:'1px solid #f0f0f0' }}>
      <Box sx={{ display:'flex', alignItems:'center', gap:1.5, py:0.9, cursor:'pointer' }} onClick={()=>setOpen(!open)}>
        <ExpandMoreIcon sx={{ fontSize:18, color:'text.disabled', transform:open?'rotate(180deg)':'none', transition:'.2s' }} />
        <Typography sx={{ width:230, flexShrink:0, fontSize:'0.88rem', fontWeight:700 }}>{cc.label}</Typography>
        <Tooltip arrow title={`Total ${cc.label} cost across the chain's ${cc.num_facilities} facilities (FY ${year})`}>
          <Typography sx={{ width:84, flexShrink:0, fontSize:'0.82rem', fontWeight:700, color:'text.primary' }}>{usd(cc.total_cost)}</Typography>
        </Tooltip>
        <Box sx={{ width:150, flexShrink:0 }}>
          {cc.num_with_rp > 0
            ? <Chip size="small" label={`${cc.num_with_rp} of ${cc.num_facilities} declare RP`} color="warning" variant="outlined" sx={{ height:20, '& .MuiChip-label':{ px:0.75, fontSize:'0.68rem', fontWeight:700 } }} />
            : <Typography sx={{ fontSize:'0.72rem', color:'text.disabled' }}>none declare RP</Typography>}
        </Box>
        <Box sx={{ flex:1 }} />
        <Sev r={cc.num_above_p90} y={yellow} g={green} />
        <Typography sx={{ width:120, textAlign:'right', fontSize:'0.72rem', color:'text.secondary' }}>
          {cc.num_above_p90>0?`${cc.num_above_p90} above p90`:cc.num_above_p70>0?`${cc.num_above_p70} above p70`:''}
        </Typography>
      </Box>
      {open && (
        <Box sx={{ pl:4.5, pb:2.5, display:'flex', gap:3, flexWrap:'wrap', alignItems:'flex-start' }}>
          <Box sx={{ flex:'0 0 560px', maxWidth:'100%' }}>
            <Typography sx={{ fontSize:'0.7rem', color:'text.secondary', mb:0.5 }}>Market vs the chain — ▲ related-party facility, ○ not; colored by where it falls</Typography>
            <ChainCurve cc={cc} width={540} height={150} visibleKeys={(['all','rp_only','no_rp'] as const).filter(k=>visible[k])} />
            {/* Clickable legend = show/hide filter for each series; kept on a single line */}
            <Box sx={{ display:'flex', gap:0.75, mt:1, flexWrap:'nowrap' }}>
              {(['all','rp_only','no_rp'] as const).map(k=>{
                const dist=(cc.distribution as any)[k]; const on=visible[k];
                return (
                  <Box key={k} onClick={(e)=>{ e.stopPropagation(); setVisible(v=>({ ...v, [k]: !v[k] })); }}
                    sx={{ display:'flex', alignItems:'center', gap:0.5, cursor:'pointer', px:0.85, py:0.45, borderRadius:1.5,
                          flexShrink:0, whiteSpace:'nowrap',
                          border:'1px solid', borderColor: on?GROUP_COLOR[k]:'divider',
                          backgroundColor: on?`${GROUP_COLOR[k]}14`:'transparent', opacity: on?1:0.45,
                          transition:'opacity 0.15s', userSelect:'none' }}>
                    <Box sx={{ width:15, flexShrink:0, borderTop:`2.5px ${k==='rp_only'?'dashed':k==='no_rp'?'dotted':'solid'} ${GROUP_COLOR[k]}` }} />
                    <Typography sx={{ fontSize:'0.76rem', fontWeight:700, color: on?'text.primary':'text.disabled' }}>
                      {k==='all'?'All':k==='rp_only'?'RP-only':'No-RP'}{dist?.count!=null?` (${dist.count})`:''}
                    </Typography>
                    {dist?.median!=null && <Typography sx={{ fontSize:'0.7rem', color:'text.secondary' }}>med {money(dist.median)}</Typography>}
                  </Box>
                );
              })}
            </Box>
          </Box>
          <Box sx={{ flex:'1 1 380px', minWidth:340 }}>
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'0.78rem' }}>
              <thead><tr style={{ background:'#fafbfc', color:'#555' }}>
                <th style={{ textAlign:'left', padding:'5px 8px', fontWeight:700, borderBottom:'2px solid #e0e0e0' }}>Facility</th>
                <th style={{ textAlign:'right', padding:'5px 8px', fontWeight:700, borderBottom:'2px solid #e0e0e0' }}>Cost (FY {year})</th>
                <th style={{ textAlign:'right', padding:'5px 8px', fontWeight:700, borderBottom:'2px solid #e0e0e0' }}>$/res-day</th>
                <th style={{ textAlign:'center', padding:'5px 8px', fontWeight:700, borderBottom:'2px solid #e0e0e0' }}>RP?</th>
                <th style={{ textAlign:'right', padding:'5px 8px', fontWeight:700, borderBottom:'2px solid #e0e0e0' }}>vs All</th>
                <th style={{ textAlign:'right', padding:'5px 8px', fontWeight:700, borderBottom:'2px solid #e0e0e0' }}>vs RP-only</th>
                <th style={{ textAlign:'right', padding:'5px 8px', fontWeight:700, borderBottom:'2px solid #e0e0e0' }}>vs No-RP</th>
              </tr></thead>
              <tbody>
                {sortedFacs.map(m=>(
                  <tr key={m.ccn} style={{ borderBottom:'1px solid #f3f3f3' }}>
                    <td style={{ padding:'4px 8px', maxWidth:200, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                      <Box component="a" href={`/schemes/snf-understaffing/facility/${m.ccn}?tab=related_parties`} target="_blank" rel="noopener noreferrer"
                           sx={{ color:'primary.main', textDecoration:'none', fontWeight:600, '&:hover':{ textDecoration:'underline' } }}>
                        <Tooltip arrow title={`${m.ccn} — open facility`}><span>{m.name}</span></Tooltip>
                      </Box>
                    </td>
                    <td style={{ padding:'4px 8px', textAlign:'right', fontWeight:700 }}>
                      <Tooltip arrow title={m.total!=null&&m.resident_days?`${usd(m.total)} ÷ ${m.resident_days.toLocaleString()} resident-days`:''}><span>{usd(m.total)}</span></Tooltip>
                    </td>
                    <td style={{ padding:'4px 8px', textAlign:'right', color:'#555' }}>{money(m.value)}</td>
                    <td style={{ padding:'4px 8px', textAlign:'center' }}>{m.has_rp?'✓':'—'}</td>
                    <td style={{ padding:'4px 8px', textAlign:'right' }}>{pcell(m.pctile_all)}</td>
                    <td style={{ padding:'4px 8px', textAlign:'right' }}>{pcell(m.pctile_rp)}</td>
                    <td style={{ padding:'4px 8px', textAlign:'right' }}>{pcell(m.pctile_no_rp)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Box>
        </Box>
      )}
    </Box>
  );
};

const OwnerGroupCostReportTab: React.FC<{ groupId:string; state?:string; schemeId?:string }> = ({ groupId, state, schemeId }) => {
  const [data,setData] = useState<Payload|null>(null);
  const [loading,setLoading] = useState(true);
  const [year,setYear] = useState<number|null>(null);

  useEffect(()=>{
    let active=true; setLoading(true);
    const p = new URLSearchParams(); if(state) p.set('state',state); if(year) p.set('year',String(year));
    api.get(`/schemes/${schemeId}/understaffing/owner-group/${encodeURIComponent(groupId)}/cost-report-analysis/?${p.toString()}`)
      .then(res=>{ if(active){ setData(res.data); if(year==null && res.data?.year) setYear(res.data.year); } })
      .catch(err=>console.error('Failed to fetch owner-group cost report analysis',err))
      .finally(()=>{ if(active) setLoading(false); });
    return ()=>{ active=false; };
  },[groupId,state,schemeId,year]);

  if(loading && !data) return <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}><CircularProgress size={32}/></Box>;
  if(!data || !data.cost_centers.length) return <Typography color="text.secondary" sx={{p:2}}>No cost report data for this chain.</Typography>;

  const chip = (label:string, v:number) => (
    <Box key={v} onClick={()=>setYear(v)} sx={{ px:1.25, py:0.3, borderRadius:5, fontSize:'0.74rem', fontWeight:600, cursor:'pointer',
      backgroundColor: (year??data.year)===v?'primary.main':'grey.100', color:(year??data.year)===v?'#fff':'text.primary' }}>{label}</Box>
  );

  return (
    <Box>
      <Box sx={{ display:'flex', alignItems:'center', gap:1, mb:0.5, flexWrap:'wrap' }}>
        <Typography variant="body2" color="text.secondary">
          How the chain's {data.num_facilities} facilities stack up per cost center vs <strong>all</strong> / <strong>non-RP</strong> / <strong>RP-only</strong> facilities
          {data.state?` in ${data.state}`:''}. Click a cost center for the distribution.
        </Typography>
        <Box sx={{ flex:1 }} />
        {data.years.slice().reverse().map(y=>chip(String(y),y))}
      </Box>
      <Box sx={{ border:'1px solid', borderColor:'divider', borderRadius:2, px:1.5, py:0.5, mt:1 }}>
        {data.cost_centers.map(cc=> <CostCenterRow key={cc.key} cc={cc} year={year??data.year} />)}
      </Box>
    </Box>
  );
};

export default OwnerGroupCostReportTab;
